import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Order } from '@lr/shared';
import { apiFetch } from '../../lib/api';
import { StatusBadge } from './AdminDashboard';

type IdStatus = 'not_submitted' | 'pending' | 'approved' | 'rejected';

type OrderRow = Order & {
  user?: {
    email: string;
    first_name: string | null;
    last_name: string | null;
    id_status?: IdStatus | null;
  } | null;
  order_items?: { id: string }[];
};

type StageFilter =
  | 'all'
  | 'awaiting_id'
  | 'to_ship'
  | 'out'
  | 'return_requested'
  | 'return_label_sent'
  | 'overdue'
  | 'returned';

const STAGE_LABELS: Record<StageFilter, string> = {
  all: 'All',
  awaiting_id: 'Waiting on ID',
  to_ship: 'Ready to ship',
  out: 'Out on rental',
  return_requested: 'Return requested',
  return_label_sent: 'Return label sent',
  overdue: 'Overdue',
  returned: 'Returned'
};

/**
 * Compute the rental "stage" for an order — a higher-level grouping than
 * the raw status enum. This is what admins actually think about when they
 * scan the orders list ("what needs my action right now?").
 */
function stageOf(o: OrderRow, now: number): StageFilter {
  const idApproved = (o.user?.id_status ?? 'not_submitted') === 'approved';

  if (o.status === 'returned' || o.status === 'cancelled') return 'returned';
  if (o.status === 'return_label_sent') return 'return_label_sent';
  if (o.status === 'returning') return 'return_requested';

  // Overdue: shipped/delivered AND past return_due_date.
  if (
    ['shipped', 'delivered'].includes(o.status) &&
    o.return_due_date &&
    new Date(o.return_due_date).getTime() < now
  ) {
    return 'overdue';
  }

  if (['shipped', 'delivered'].includes(o.status)) return 'out';

  if (['confirmed', 'preparing'].includes(o.status)) {
    return idApproved ? 'to_ship' : 'awaiting_id';
  }
  return 'all';
}

export function AdminOrders() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<StageFilter>('all');
  const [requestingId, setRequestingId] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<OrderRow[]>('/api/admin/orders')
      .then((data) =>
        // Only clothing rentals — exclude pure credit-bundle purchases
        // (handled in the Credit Sales screen).
        setRows(data.filter((o) => (o.order_items?.length ?? 0) > 0))
      )
      .finally(() => setLoading(false));
  }, []);

  const now = Date.now();

  const counts = useMemo(() => {
    const c: Record<StageFilter, number> = {
      all: rows.length,
      awaiting_id: 0,
      to_ship: 0,
      out: 0,
      return_requested: 0,
      return_label_sent: 0,
      overdue: 0,
      returned: 0
    };
    for (const r of rows) {
      const s = stageOf(r, now);
      c[s]++;
    }
    return c;
  }, [rows, now]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (stage !== 'all' && stageOf(r, now) !== stage) return false;
      if (!q) return true;
      return (
        r.order_number.toLowerCase().includes(q) ||
        (r.user?.email ?? '').toLowerCase().includes(q) ||
        `${r.user?.first_name ?? ''} ${r.user?.last_name ?? ''}`
          .toLowerCase()
          .includes(q)
      );
    });
  }, [rows, search, stage, now]);

  async function requestId(userId: string) {
    setRequestingId(userId);
    try {
      await apiFetch(`/api/admin/users/${userId}/request-id`, {
        method: 'POST'
      });
      window.alert('ID request email sent.');
    } catch (e) {
      window.alert(
        e instanceof Error ? e.message : 'Failed to send ID request.'
      );
    } finally {
      setRequestingId(null);
    }
  }

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Orders</h1>
          <div className="sub">
            Clothing rentals. Credit purchases are in Credit Sales.
          </div>
        </div>
      </div>

      <div className="a-card">
        <div className="a-toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="a-search" style={{ flex: 1, minWidth: 200 }}>
            <span className="ic">
              <SearchIcon />
            </span>
            <input
              placeholder="Search by order #, name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {(
            [
              'all',
              'awaiting_id',
              'to_ship',
              'out',
              'return_requested',
              'return_label_sent',
              'overdue',
              'returned'
            ] as StageFilter[]
          ).map((k) => (
            <button
              key={k}
              type="button"
              className={`a-filter${stage === k ? ' active' : ''}`}
              onClick={() => setStage(k)}
              style={
                k === 'overdue' && counts.overdue > 0
                  ? { color: 'var(--a-danger, #b3261e)' }
                  : undefined
              }
            >
              {STAGE_LABELS[k]}
              <span style={{ opacity: 0.7, marginLeft: 4 }}>
                {counts[k]}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="a-empty">
            <div className="h">Nothing here</div>
            <div className="p">
              {rows.length === 0
                ? 'Once customers check out, orders appear here.'
                : 'Try a different filter or clear the search.'}
            </div>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table admin-orders-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th style={{ width: 110 }}>ID</th>
                  <th>Stage</th>
                  <th style={{ width: 70 }}>Items</th>
                  <th style={{ width: 90 }}>Credits</th>
                  <th style={{ width: 140 }}>Return due</th>
                  <th style={{ width: 120 }}>Placed</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const idStatus = o.user?.id_status ?? 'not_submitted';
                  const idApproved = idStatus === 'approved';
                  const orderStage = stageOf(o, now);
                  return (
                    <tr
                      key={o.id}
                      className="clickable"
                      onClick={() => navigate(`/admin/orders/${o.id}`)}
                    >
                      <td className="id">
                        <Link
                          to={`/admin/orders/${o.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {o.order_number}
                        </Link>
                        {orderStage === 'overdue' && (
                          <div
                            style={{
                              fontSize: 10,
                              color: 'var(--a-danger, #b3261e)',
                              fontWeight: 700,
                              marginTop: 2,
                              letterSpacing: '0.06em'
                            }}
                          >
                            OVERDUE
                          </div>
                        )}
                        {orderStage === 'return_requested' && (
                          <div
                            style={{
                              fontSize: 10,
                              color: 'var(--a-info, #1f5fa8)',
                              fontWeight: 700,
                              marginTop: 2,
                              letterSpacing: '0.06em'
                            }}
                          >
                            RETURN LABEL REQUESTED
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {(o.user?.first_name ?? '') +
                            ' ' +
                            (o.user?.last_name ?? '')}
                        </div>
                        <div className="id">{o.user?.email}</div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          <IdStatusBadge status={idStatus} />
                          {!idApproved && o.user && (
                            <button
                              type="button"
                              className="btn-ghost"
                              disabled={requestingId === o.user_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                void requestId(o.user_id);
                              }}
                              style={{
                                fontSize: 11,
                                padding: '4px 8px',
                                color: 'var(--a-info, #1f5fa8)'
                              }}
                            >
                              {requestingId === o.user_id
                                ? 'Sending…'
                                : 'Email request'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="num">{o.order_items?.length ?? 0}</td>
                      <td className="num">{o.credits_used}</td>
                      <td>
                        <DueCell order={o} now={now} />
                      </td>
                      <td>
                        {new Date(o.created_at).toLocaleDateString('en-GB')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function DueCell({ order, now }: { order: OrderRow; now: number }) {
  if (order.status === 'returned' || order.status === 'cancelled') {
    return (
      <span style={{ color: 'var(--a-ink-soft)' }}>
        {order.returned_at
          ? `returned ${new Date(order.returned_at).toLocaleDateString('en-GB')}`
          : '—'}
      </span>
    );
  }
  if (!order.return_due_date) {
    return (
      <span style={{ color: 'var(--a-ink-soft)', fontSize: 12 }}>
        starts on delivery
      </span>
    );
  }
  const due = new Date(order.return_due_date).getTime();
  const days = Math.floor((due - now) / (1000 * 60 * 60 * 24));
  const dateStr = new Date(order.return_due_date).toLocaleDateString('en-GB');
  if (days < 0) {
    return (
      <span style={{ color: 'var(--a-danger, #b3261e)', fontWeight: 700 }}>
        {Math.abs(days)} {Math.abs(days) === 1 ? 'day' : 'days'} overdue
        <div style={{ fontSize: 11, fontWeight: 400 }}>{dateStr}</div>
      </span>
    );
  }
  if (days <= 3) {
    return (
      <span style={{ color: 'var(--a-warn, #a85f00)', fontWeight: 600 }}>
        {days === 0 ? 'due today' : `${days} ${days === 1 ? 'day' : 'days'} left`}
        <div style={{ fontSize: 11, fontWeight: 400 }}>{dateStr}</div>
      </span>
    );
  }
  return (
    <span>
      {days} days left
      <div
        style={{
          fontSize: 11,
          color: 'var(--a-ink-soft)',
          fontWeight: 400
        }}
      >
        {dateStr}
      </div>
    </span>
  );
}

function IdStatusBadge({ status }: { status: IdStatus }) {
  const map: Record<IdStatus, { label: string; cls: string }> = {
    approved: { label: 'Verified', cls: 'badge-good' },
    pending: { label: 'In review', cls: 'badge-warn' },
    rejected: { label: 'Rejected', cls: 'badge-danger' },
    not_submitted: { label: 'Not submitted', cls: 'badge-neutral' }
  };
  const { label, cls } = map[status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}
